import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, User, Mail, Phone, MapPin, Briefcase, DollarSign, FileText, Globe, Lock, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useToast } from '../../ui/use-toast';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { getDomainFilterParams } from '../../../hooks/useDomainQuery';
import { updateUserPassword } from '../../../services/adminAuthService';
import { v4 as uuidv4 } from 'uuid';

interface PractitionerModalProps {
  isOpen: boolean;
  onClose: (refresh?: boolean) => void;
  practitioner?: any | null;
}

const SPECIALTIES_OPTIONS = [
  'Psychologie clinique',
  'Psychothérapie',
  'Thérapie cognitive-comportementale (TCC)',
  'Psychanalyse',
  'Thérapie familiale',
  'Thérapie de couple',
  'Psychologie de l\'enfant',
  'Psychologie de l\'adolescent',
  'Gestion du stress',
  'Troubles anxieux',
  'Dépression',
  'Traumatismes',
  'Addictions',
  'Troubles alimentaires',
  'Développement personnel'
];

const LANGUAGES_OPTIONS = [
  'Français',
  'Anglais',
  'Espagnol',
  'Allemand',
  'Arabe',
  'Fulfulde',
  'Ewondo',
  'Duala'
];

const CONSULTATION_MODES = [
  { value: 'video', label: 'Visioconférence' },
  { value: 'audio', label: 'Audio' },
  { value: 'chat', label: 'Chat' },
  { value: 'in-person', label: 'En présentiel' }
];

export const PractitionerModal: React.FC<PractitionerModalProps> = ({
  isOpen,
  onClose,
  practitioner
}) => {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    city: '',
    country: 'Cameroun',
    timezone: 'Africa/Douala',
    bio: '',
    specialties: [] as string[],
    languages: ['Français'] as string[],
    consultation_modes: ['video'] as string[],
    base_rate_per_session: '10000',
    currency: 'XAF',
    accepts_sliding_scale: false,
    accepts_new_patients: true,
    max_patients_per_day: '8',
    years_of_experience: '',
    education: '',
    license_number: '',
    verification_status: 'pending'
  });

  useEffect(() => {
    if (practitioner) {
      setFormData({
        full_name: practitioner.full_name || '',
        email: practitioner.email || '',
        password: '',
        confirmPassword: '',
        phone: practitioner.phone || '',
        city: practitioner.city || '',
        country: practitioner.country || 'Cameroun',
        timezone: practitioner.timezone || 'Africa/Douala',
        bio: practitioner.bio || '',
        specialties: practitioner.specialties || [],
        languages: practitioner.languages || ['Français'],
        consultation_modes: practitioner.consultation_modes || ['video'],
        base_rate_per_session: practitioner.base_rate_per_session?.toString() || '10000',
        currency: practitioner.currency || 'XAF',
        accepts_sliding_scale: practitioner.accepts_sliding_scale || false,
        accepts_new_patients: practitioner.accepts_new_patients !== false,
        max_patients_per_day: practitioner.max_patients_per_day?.toString() || '8',
        years_of_experience: practitioner.years_of_experience?.toString() || '',
        education: practitioner.education || '',
        license_number: practitioner.license_number || '',
        verification_status: practitioner.verification_status || 'pending'
      });
    } else {
      // Reset form for new practitioner
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        city: '',
        country: 'Cameroun',
        timezone: 'Africa/Douala',
        bio: '',
        specialties: [],
        languages: ['Français'],
        consultation_modes: ['video'],
        base_rate_per_session: '10000',
        currency: 'XAF',
        accepts_sliding_scale: false,
        accepts_new_patients: true,
        max_patients_per_day: '8',
        years_of_experience: '',
        education: '',
        license_number: '',
        verification_status: 'pending'
      });
    }
  }, [practitioner]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.full_name.trim()) {
      toast({
        title: 'Erreur',
        description: 'Le nom complet est requis',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.email.trim()) {
      toast({
        title: 'Erreur',
        description: 'L\'email est requis',
        variant: 'destructive'
      });
      return;
    }

    if (formData.specialties.length === 0) {
      toast({
        title: 'Erreur',
        description: 'Veuillez sélectionner au moins une spécialité',
        variant: 'destructive'
      });
      return;
    }

    // Validation du mot de passe pour création
    if (!practitioner) {
      if (!formData.password || formData.password.length < 6) {
        toast({
          title: 'Erreur',
          description: 'Le mot de passe doit contenir au moins 6 caractères',
          variant: 'destructive'
        });
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        toast({
          title: 'Erreur',
          description: 'Les mots de passe ne correspondent pas',
          variant: 'destructive'
        });
        return;
      }
    }

    // Validation du mot de passe pour modification (si fourni)
    if (practitioner && formData.password) {
      if (formData.password.length < 6) {
        toast({
          title: 'Erreur',
          description: 'Le mot de passe doit contenir au moins 6 caractères',
          variant: 'destructive'
        });
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        toast({
          title: 'Erreur',
          description: 'Les mots de passe ne correspondent pas',
          variant: 'destructive'
        });
        return;
      }
    }

    try {
      setIsSaving(true);

      const { domain, tenantId } = getDomainFilterParams();
      if (!tenantId) return;

      const practitionerData = {
        full_name: formData.full_name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim() || null,
        city: formData.city.trim() || null,
        country: formData.country,
        timezone: formData.timezone,
        bio: formData.bio.trim() || null,
        specialties: formData.specialties,
        languages: formData.languages,
        consultation_modes: formData.consultation_modes,
        base_rate_per_session: parseFloat(formData.base_rate_per_session),
        currency: formData.currency,
        accepts_sliding_scale: formData.accepts_sliding_scale,
        accepts_new_patients: formData.accepts_new_patients,
        max_patients_per_day: parseInt(formData.max_patients_per_day),
        years_of_experience: formData.years_of_experience ? parseInt(formData.years_of_experience) : null,
        education: formData.education.trim() || null,
        license_number: formData.license_number.trim() || null,
        verification_status: formData.verification_status,
        is_active: true,
        tenant_id: tenantId,
        domain: domain,
        updated_at: new Date().toISOString()
      };

      if (practitioner) {
        // Mise à jour d'un praticien existant
        
        // Mettre à jour le mot de passe si fourni (via Edge Function)
        if (formData.password && formData.password.trim() && practitioner.user_id) {
          console.log('🔐 Mise à jour du mot de passe via Edge Function...');
          
          const passwordResult = await updateUserPassword({
            userId: practitioner.user_id,
            password: formData.password,
            email: formData.email,
            name: formData.full_name
          });

          if (!passwordResult.success) {
            console.error('Erreur lors de la mise à jour du mot de passe:', passwordResult.error);
            toast({
              title: 'Avertissement',
              description: 'Le praticien a été mis à jour mais le mot de passe n\'a pas pu être changé.',
              variant: 'default',
            });
          } else {
            console.log('✅ Mot de passe mis à jour avec succès');
          }
        }

        // Mettre à jour le profil praticien
        const { error } = await supabase
          .from('psy_practitioners')
          .update(practitionerData)
          .eq('id', practitioner.id);

        if (error) throw error;

        toast({
          title: 'Succès',
          description: `Le praticien a été mis à jour avec succès${formData.password ? ' (mot de passe inclus)' : ''}`,
        });
      } else {
        // Création d'un nouveau praticien avec compte auth
        
        // Sauvegarder la session admin actuelle
        const { data: { session: adminSession } } = await supabase.auth.getSession();
        
        if (!adminSession) {
          throw new Error('Aucune session admin active');
        }
        
        console.log('💾 Sauvegarde de la session admin avant création utilisateur');
        console.log('Admin user ID:', adminSession.user.id);
        console.log('Admin email:', adminSession.user.email);
        
        // Créer le compte utilisateur
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: {
              name: formData.full_name,
              role: 'practitioner',
              tenant_id: tenantId.toString(),
              domain: domain
            }
          }
        });

        if (authError) {
          console.error('Erreur signUp:', authError);
          throw authError;
        }

        console.log('✅ Compte créé:', authData.user?.id);
        
        // Restaurer immédiatement la session admin
        console.log('🔄 Restauration de la session admin...');
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: adminSession.access_token,
          refresh_token: adminSession.refresh_token
        });

        if (sessionError) {
          console.error('Erreur restauration session:', sessionError);
        } else {
          console.log('✅ Session admin restaurée');
        }
        
        // Vérifier que la session est bien restaurée
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        console.log('Session actuelle après restauration:', {
          userId: currentSession?.user?.id,
          email: currentSession?.user?.email
        });

        if (authData.user) {
          // Créer le profil praticien avec l'ID utilisateur
          const newPractitionerId = uuidv4();
          const { error } = await supabase
            .from('psy_practitioners')
            .insert([{
              id: newPractitionerId,
              user_id: authData.user.id,
              ...practitionerData,
              created_at: new Date().toISOString()
            }]);

          if (error) throw error;

          console.log('✅ Praticien créé avec succès !');
          
          toast({
            title: 'Succès',
            description: `Le praticien ${formData.full_name} a été créé avec succès. Il peut maintenant se connecter.`,
          });
        }
      }

      onClose(true); // Refresh the list
    } catch (error: any) {
      console.error('Error saving practitioner:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Une erreur est survenue lors de l\'enregistrement',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleSpecialty = (specialty: string) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter(s => s !== specialty)
        : [...prev.specialties, specialty]
    }));
  };

  const toggleLanguage = (language: string) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(language)
        ? prev.languages.filter(l => l !== language)
        : [...prev.languages, language]
    }));
  };

  const toggleConsultationMode = (mode: string) => {
    setFormData(prev => ({
      ...prev,
      consultation_modes: prev.consultation_modes.includes(mode)
        ? prev.consultation_modes.filter(m => m !== mode)
        : [...prev.consultation_modes, mode]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-[#001122] to-[#000811] rounded-lg border border-white/10 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-white/10 sticky top-0 bg-gradient-to-br from-[#001122] to-[#000811] z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <User className="h-6 w-6 text-blue-400" />
              {practitioner ? 'Modifier le praticien' : 'Nouveau praticien'}
            </h2>
            <button
              onClick={() => onClose()}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informations personnelles */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-blue-400" />
              Informations Personnelles
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 block mb-2">
                  Nom complet <span className="text-red-400">*</span>
                </label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Dr. Jean Dupont"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 block mb-2">
                  Email <span className="text-red-400">*</span>
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="email@example.com"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 block mb-2">
                  Mot de passe {!practitioner && <span className="text-red-400">*</span>}
                  {practitioner && <span className="text-xs text-gray-500 ml-2">(laisser vide pour ne pas modifier)</span>}
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-white/5 border-white/10 text-white pr-10"
                    placeholder="••••••••"
                    required={!practitioner}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400 block mb-2">
                  Confirmer le mot de passe {!practitioner && <span className="text-red-400">*</span>}
                </label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="bg-white/5 border-white/10 text-white pr-10"
                    placeholder="••••••••"
                    required={!practitioner}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400 block mb-2">Téléphone</label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="+237 6XX XXX XXX"
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 block mb-2">Ville</label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Douala"
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 block mb-2">Pays</label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Cameroun"
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 block mb-2">Fuseau horaire</label>
                <Input
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Africa/Douala"
                />
              </div>

              <div className="col-span-2">
                <label className="text-sm text-gray-400 block mb-2">Biographie</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent min-h-[100px]"
                  placeholder="Présentez votre parcours et votre approche thérapeutique..."
                />
              </div>
            </div>
          </div>

          {/* Spécialités */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-blue-400" />
              Spécialités <span className="text-red-400">*</span>
            </h3>
            <div className="flex flex-wrap gap-2">
              {SPECIALTIES_OPTIONS.map((specialty) => (
                <Badge
                  key={specialty}
                  onClick={() => toggleSpecialty(specialty)}
                  className={`cursor-pointer transition-all ${
                    formData.specialties.includes(specialty)
                      ? 'bg-blue-500/30 text-blue-300 border-blue-500/50 hover:bg-blue-500/40'
                      : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                  }`}
                >
                  {specialty}
                </Badge>
              ))}
            </div>
          </div>

          {/* Langues */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-400" />
              Langues parlées
            </h3>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES_OPTIONS.map((language) => (
                <Badge
                  key={language}
                  onClick={() => toggleLanguage(language)}
                  className={`cursor-pointer transition-all ${
                    formData.languages.includes(language)
                      ? 'bg-green-500/30 text-green-300 border-green-500/50 hover:bg-green-500/40'
                      : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                  }`}
                >
                  {language}
                </Badge>
              ))}
            </div>
          </div>

          {/* Modes de consultation */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-400" />
              Modes de consultation
            </h3>
            <div className="flex flex-wrap gap-2">
              {CONSULTATION_MODES.map((mode) => (
                <Badge
                  key={mode.value}
                  onClick={() => toggleConsultationMode(mode.value)}
                  className={`cursor-pointer transition-all ${
                    formData.consultation_modes.includes(mode.value)
                      ? 'bg-purple-500/30 text-purple-300 border-purple-500/50 hover:bg-purple-500/40'
                      : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                  }`}
                >
                  {mode.label}
                </Badge>
              ))}
            </div>
          </div>

          {/* Tarification */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-blue-400" />
              Tarification
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-gray-400 block mb-2">Tarif par session</label>
                <Input
                  type="number"
                  value={formData.base_rate_per_session}
                  onChange={(e) => setFormData({ ...formData, base_rate_per_session: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="10000"
                  min="0"
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 block mb-2">Devise</label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                >
                  <option value="XAF" className="bg-gray-900">XAF (Franc CFA)</option>
                  <option value="EUR" className="bg-gray-900">EUR (Euro)</option>
                  <option value="USD" className="bg-gray-900">USD (Dollar)</option>
                </select>
              </div>

              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="sliding_scale"
                  checked={formData.accepts_sliding_scale}
                  onChange={(e) => setFormData({ ...formData, accepts_sliding_scale: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-white/5 border-white/10 rounded focus:ring-blue-500"
                />
                <label htmlFor="sliding_scale" className="text-sm text-gray-400">
                  Tarif dégressif
                </label>
              </div>
            </div>
          </div>

          {/* Disponibilité */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-400" />
              Disponibilité
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 block mb-2">Patients max par jour</label>
                <Input
                  type="number"
                  value={formData.max_patients_per_day}
                  onChange={(e) => setFormData({ ...formData, max_patients_per_day: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="8"
                  min="1"
                />
              </div>

              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="accepts_new"
                  checked={formData.accepts_new_patients}
                  onChange={(e) => setFormData({ ...formData, accepts_new_patients: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-white/5 border-white/10 rounded focus:ring-blue-500"
                />
                <label htmlFor="accepts_new" className="text-sm text-gray-400">
                  Accepte de nouveaux patients
                </label>
              </div>
            </div>
          </div>

          {/* Qualifications */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-400" />
              Qualifications
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 block mb-2">Années d'expérience</label>
                <Input
                  type="number"
                  value={formData.years_of_experience}
                  onChange={(e) => setFormData({ ...formData, years_of_experience: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="5"
                  min="0"
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 block mb-2">Numéro de licence</label>
                <Input
                  value={formData.license_number}
                  onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="PSY-12345"
                />
              </div>

              <div className="col-span-2">
                <label className="text-sm text-gray-400 block mb-2">Formation</label>
                <textarea
                  value={formData.education}
                  onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent min-h-[80px]"
                  placeholder="Master en Psychologie Clinique, Université de..."
                />
              </div>
            </div>
          </div>

          {/* Statut de vérification */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Statut de vérification</h3>
            <select
              value={formData.verification_status}
              onChange={(e) => setFormData({ ...formData, verification_status: e.target.value })}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent"
            >
              <option value="pending" className="bg-gray-900">En attente</option>
              <option value="approved" className="bg-gray-900">Approuvé</option>
              <option value="rejected" className="bg-gray-900">Rejeté</option>
              <option value="suspended" className="bg-gray-900">Suspendu</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-4 border-t border-white/10">
            <Button
              type="button"
              onClick={() => onClose()}
              variant="outline"
              className="border-white/10 text-gray-300 hover:bg-white/5"
              disabled={isSaving}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:opacity-90"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {practitioner ? 'Mettre à jour' : 'Créer le praticien'}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
